import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { resolveLoungeUrl } from "@/lib/loungeUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Upload, Trash2, Save, Plus, User2 } from "lucide-react";

export function AmbassadorsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nw, setNw] = useState({ name: "", discipline: "", social: "", url: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/lounge/ambassadors").then(({ data }) => setItems(data.ambassadors || [])).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!nw.name.trim()) return toast.error("Inserisci il nome");
    setBusy(true);
    const fd = new FormData();
    fd.append("name", nw.name); fd.append("discipline", nw.discipline); fd.append("social", nw.social); fd.append("file", file);
    try { await api.post("/admin/lounge/ambassadors/upload", fd); toast.success("Ambassador aggiunto"); setNw({ name: "", discipline: "", social: "", url: "" }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const addByUrl = async () => {
    if (!nw.name.trim()) return toast.error("Inserisci il nome");
    setBusy(true);
    try { await api.post("/admin/lounge/ambassadors", { name: nw.name, discipline: nw.discipline, social: nw.social, photo: nw.url.trim() }); toast.success("Ambassador aggiunto"); setNw({ name: "", discipline: "", social: "", url: "" }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const save = async (a) => { try { await api.patch(`/admin/lounge/ambassadors/${a.id}`, { name: a.name, discipline: a.discipline, social: a.social, photo: a.photo, visible: a.visible }); toast.success("Salvato"); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const del = async (id) => { try { await api.delete(`/admin/lounge/ambassadors/${id}`); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const upd = (id, k, v) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  if (loading) return <div className="flex items-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;

  return (
    <div className="space-y-6" data-testid="ambassadors-tab">
      <section className="rounded-xl border border-border bg-brema-card p-5">
        <h3 className="mb-3 text-lg font-semibold">Aggiungi ambassador</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div><Label className="mb-1.5 block text-xs">Nome</Label><Input value={nw.name} onChange={(e) => setNw((n) => ({ ...n, name: e.target.value }))} data-testid="amb-new-name" /></div>
          <div><Label className="mb-1.5 block text-xs">Disciplina</Label><Input value={nw.discipline} onChange={(e) => setNw((n) => ({ ...n, discipline: e.target.value }))} data-testid="amb-new-discipline" /></div>
          <div><Label className="mb-1.5 block text-xs">Social</Label><Input value={nw.social} onChange={(e) => setNw((n) => ({ ...n, social: e.target.value }))} data-testid="amb-new-social" /></div>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]"><Label className="mb-1.5 block text-xs">URL foto (oppure carica)</Label><Input value={nw.url} onChange={(e) => setNw((n) => ({ ...n, url: e.target.value }))} placeholder="https://…" data-testid="amb-new-url" /></div>
          <input type="file" accept="image/*" hidden ref={fileRef} onChange={(e) => uploadFile(e.target.files?.[0])} />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="amb-upload-btn"><Upload className="mr-1.5 h-4 w-4" /> Carica foto</Button>
          <Button onClick={addByUrl} disabled={busy} className="bg-brema-red text-white hover:bg-brema-red/90" data-testid="amb-add-url-btn"><Plus className="mr-1.5 h-4 w-4" /> Aggiungi</Button>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((a) => (
          <div key={a.id} className="rounded-xl border border-border bg-brema-card p-4" data-testid={`amb-item-${a.id}`}>
            <div className="flex gap-3">
              <div className="h-24 w-20 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
                {a.photo ? <img src={resolveLoungeUrl(a.photo)} alt={a.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted-foreground"><User2 className="h-5 w-5" /></div>}
              </div>
              <div className="flex-1 space-y-2">
                <Input value={a.name || ""} onChange={(e) => upd(a.id, "name", e.target.value)} placeholder="Nome" data-testid={`amb-name-${a.id}`} />
                <Input value={a.discipline || ""} onChange={(e) => upd(a.id, "discipline", e.target.value)} placeholder="Disciplina" data-testid={`amb-discipline-${a.id}`} />
                <Input value={a.social || ""} onChange={(e) => upd(a.id, "social", e.target.value)} placeholder="Social" data-testid={`amb-social-${a.id}`} />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={a.visible !== false} onCheckedChange={(v) => upd(a.id, "visible", !!v)} data-testid={`amb-visible-${a.id}`} /> Visibile</label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => save(a)} data-testid={`amb-save-${a.id}`}><Save className="mr-1.5 h-4 w-4" /> Salva</Button>
                    <Button variant="ghost" size="sm" onClick={() => del(a.id)} data-testid={`amb-delete-${a.id}`}><Trash2 className="h-4 w-4 text-brema-red" /></Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
